// Face sets: which eye, brow and mouth sprite to draw for an emotion, a mouth shape or a blink. Every drawing style
// has its own set (src/model/faceStyles.ts): the ready-made ones in art/faces.json (scripts/faceSets.ts), custom ones
// on the style in the library. Each character picks one, its face style (Asset.faceStyle); Scratchy pen is the default.
// A sprite a set lacks comes from its front sprites, then from the default set. Sprites in one sheet share a scale,
// set by a reference sprite drawn at the spot's width.
import presets from "../../art/faces.json";
import type { Emotion, Viseme } from "../model/face";
import type { FaceAngle, FaceSprite, FaceSprites } from "../model/faceStyles";
import { DEFAULT_STYLE, PRESET_STYLES } from "../model/styles";
import { getLibrary } from "./store";

export type FacePart = { sprite: FaceSprite; ref: FaceSprite };

type FaceSet = {
  eyes(emotion: Emotion, blink: boolean): FacePart;
  brows(emotion: Emotion): FacePart;
  mouth(shape: Viseme, emotion: Emotion): FacePart;
};

const PRESETS = presets as Record<string, FaceSprites>;
export const DEFAULT_FACE_STYLE = DEFAULT_STYLE.id;
const OLD: Record<string, string> = { front: "scratchy-pen", clean: "clean-cartoon" }; // face styles saved before sets followed drawing styles
const idOf = (style: string | undefined) => (style ? OLD[style] ?? style : DEFAULT_FACE_STYLE);

// A style's sprites: ready-made, or a custom style's in the library.
const spritesOf = (style: string): FaceSprites | undefined => PRESETS[style] ?? getLibrary().styles?.find(s => s.id === style)?.faces;

// The sprite for `key` ("eyes_open") at `angle`, falling back as described above.
export function spriteOf(style: string | undefined, angle: FaceAngle, key: string): FaceSprite {
  const own = spritesOf(idOf(style)), base = PRESETS[DEFAULT_FACE_STYLE]!;
  return own?.[`${angle}_${key}`] ?? own?.[`front_${key}`] ?? base[`${angle}_${key}`] ?? base[`front_${key}`]!;
}

// These ready-made styles drew their 45° mouths narrowing the other way from their eyes, so the mouth is mirrored to match.
const MIRRORED_SIDE_MOUTHS = new Set(["rubber-hose", "crayon", "anime", "clean-cartoon"]);
export const sideMouthMirrored = (style: string | undefined) => MIRRORED_SIDE_MOUTHS.has(idOf(style));

const EYES: Record<Emotion, string> = { neutral: "open", happy: "happy", angry: "squint", sad: "lidded", surprised: "wide", smug: "lidded" };

export function faceSet(style: string | undefined, angle: FaceAngle = "front"): FaceSet {
  const f = (key: string, ref: string): FacePart => ({ sprite: spriteOf(style, angle, key), ref: spriteOf(style, angle, ref) });
  return {
    eyes: (e, blink) => f(`eyes_${blink ? "blink" : EYES[e]}`, "eyes_open"),
    brows: e => f(`brows_${e}`, "brows_neutral"),
    mouth: (m, e) => (m === "X" ? f(`rests_${e}`, "rests_neutral") : f(`mouths_${m}`, "mouths_X")),
  };
}

// The face styles there are to pick: every drawing style with a face set, ready-made first.
export function faceStyles(): { id: string; name: string; ready: boolean }[] {
  const mine = getLibrary().styles ?? [];
  return [
    ...PRESET_STYLES.filter(s => PRESETS[s.id]).map(s => ({ id: s.id, name: s.name, ready: true })),
    ...mine.map(s => ({ id: s.id, name: s.name, ready: !!s.faces })),
  ];
}
export const sameFaceStyle = (a: string | undefined, b: string | undefined) => idOf(a) === idOf(b);
export const faceStyleId = idOf;

// The face style of a character in the library (by id).
export const faceStyleOf = (charId: string) => getLibrary().assets.find(a => a.id === charId)?.faceStyle;

// Every sprite of the face styles the library's characters use (and the default), to load before playing or exporting.
export function faceSpritesInUse(): string[] {
  const styles = new Set([DEFAULT_FACE_STYLE, ...getLibrary().assets.filter(a => a.kind === "character").map(a => idOf(a.faceStyle))]);
  return [...styles].flatMap(s => Object.values(spritesOf(s) ?? {}).map(x => x.src));
}
